import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Popover,
  Avatar,
  Divider,
  CircularProgress,
} from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";
import moment from "moment";
import { getNotifications } from "../redux/reducers/notification/notificationService";
import { onMessage } from "firebase/messaging";
import { messaging } from "../firebase/firebase";
import addNotification from "react-push-notification";

const NotificationPopover = ({ open, anchorEl, onClose }) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.notification);

  const [allNotifications, setAllNotifications] = useState([]);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const limit = 5;

  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [open]);

  // Firebase messaging setup
  useEffect(() => {
    if (messaging) {
      const unsubscribe = onMessage(messaging, (payload) => {
        try {
          addNotification({
            native: true,
            title: payload?.data?.title || "New Notification",
            icon: "/logo192.png",
            message: payload?.data?.body || "You have a new notification",
            onClick: (e) => {
              e.target.close();
              if (open) {
                fetchNotifications();
              }
            },
          });
        } catch (error) {
          console.error("Error handling notification:", error);
        }
      });

      // Cleanup on unmount
      return () => {
        if (unsubscribe) unsubscribe();
      };
    }
  }, [open]);

  const fetchNotifications = async () => {
    try {
      const firstPage = 1;
      const resultAction = await dispatch(
        getNotifications({ page: firstPage, limit })
      );
      if (getNotifications.fulfilled.match(resultAction)) {
        const newData = resultAction.payload.data;
        setAllNotifications(newData);
        setPage(firstPage);
        setHasMore(newData.length >= limit);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
      setHasMore(false);
    }
  };

  const fetchMoreData = async () => {
    const nextPage = page + 1;
    try {
      const resultAction = await dispatch(
        getNotifications({ page: nextPage, limit })
      );
      if (getNotifications.fulfilled.match(resultAction)) {
        const newData = resultAction.payload.data;
        setAllNotifications((prev) => [...prev, ...newData]);
        setPage(nextPage);
        if (newData.length < limit) {
          setHasMore(false);
        }
      }
    } catch (error) {
      console.error("Failed to fetch more notifications:", error);
      setHasMore(false);
    }
  };

  // Get user initials for avatar fallback
  const getUserInitials = (user) => {
    if (!user) return "U";

    const firstInitial =
      user.firstName?.charAt(0) ||
      user.lastName?.charAt(0) ||
      user.fullName?.charAt(0) ||
      user.email?.charAt(0) ||
      "U";

    const secondInitial =
      user.lastName?.charAt(0) || user.fullName?.split(" ")[1]?.charAt(0) || "";

    return (firstInitial + secondInitial).toUpperCase();
  };

  // Get notification title fallback
  const getNotificationTitle = (notification) => {
    if (notification.title) return notification.title;

    const user = notification.fromUser;
    if (!user) return "New Notification";

    return (
      user.fullName ||
      `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
      user.email ||
      "New Notification"
    );
  };

  const renderEmptyState = () => (
    <Box
      height="80%"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          No notifications yet
        </Typography>
      )}
    </Box>
  );

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{
        sx: {
          mt: 1.5,
          width: 380,
          borderRadius: "16px",
          overflow: "hidden",
        },
      }}
    >
      <Typography variant="h6" fontWeight={600} p={2}>
        Notifications
      </Typography>

      <Box
        id="notificationScrollContainer"
        sx={{
          height: 240,
          overflow: "auto",
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "rgba(0,0,0,0.15)",
            borderRadius: 3,
          },
        }}
      >
        {allNotifications && allNotifications.length > 0 ? (
          <InfiniteScroll
            dataLength={allNotifications.length}
            next={fetchMoreData}
            hasMore={hasMore}
            loader={
              <Box py={2} display="flex" justifyContent="center">
                <CircularProgress size={20} />
              </Box>
            }
            endMessage={
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                py={2}
              >
                You've seen all notifications
              </Typography>
            }
            scrollableTarget="notificationScrollContainer"
          >
            {allNotifications.map((notification, index) => (
              <Box key={notification.id || index}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    p: 2,
                    borderRadius: 1,
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  {notification.fromUser?.profilePicture ? (
                    <Avatar
                      src={notification.fromUser.profilePicture}
                      sx={{ width: 40, height: 40, flexShrink: 0, mt: "2px" }}
                    />
                  ) : (
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        flexShrink: 0,
                        mt: "2px",
                        bgcolor: "secondary.main",
                        color: "black",
                      }}
                    >
                      {getUserInitials(notification.fromUser)}
                    </Avatar>
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        noWrap
                        sx={{ flex: 1 }}
                      >
                        {getNotificationTitle(notification)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        whiteSpace="nowrap"
                        sx={{ ml: 1, flexShrink: 0 }}
                      >
                        {moment.unix(notification.createdAt).fromNow()}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {notification.body || notification.content}
                    </Typography>
                  </Box>
                </Box>
                {index < allNotifications.length - 1 && <Divider />}
              </Box>
            ))}
          </InfiniteScroll>
        ) : (
          renderEmptyState()
        )}
      </Box>
    </Popover>
  );
};

export default NotificationPopover;
